/**
 * The company's logo, or its initial on a tinted tile when it has none — or
 * when the logo does not load (a moved favicon drew an empty, bordered square).
 * Decorative: the company's name is always written beside it.
 */
import { useState } from 'react';
import { cn } from '@tonnie/ui';

type CompanyAvatarSize = 'xs' | 'sm' | 'md';

const SIZE_CLASSES: Record<CompanyAvatarSize, string> = {
  xs: 'h-5 w-5 rounded text-2xs',
  sm: 'h-7 w-7 rounded-md text-xs',
  md: 'h-9 w-9 rounded-md text-sm',
};

interface ICompanyAvatarProps {
  name: string;
  logoUrl?: string | null;
  size?: CompanyAvatarSize;
  /** Extra classes — a ring when avatars are stacked. */
  className?: string;
}

export function CompanyAvatar({ name, logoUrl, size = 'md', className }: ICompanyAvatarProps) {
  // The URL that failed, so a new logo for the same avatar is tried again.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  if (logoUrl && logoUrl !== failedUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        aria-hidden="true"
        onError={() => setFailedUrl(logoUrl)}
        className={cn(
          'object-cover border border-border bg-background shrink-0',
          SIZE_CLASSES[size],
          className
        )}
      />
    );
  }
  return (
    <div
      aria-hidden="true"
      data-testid="company-avatar-initial"
      className={cn(
        'bg-primary/10 border border-primary/20 flex items-center justify-center font-semibold text-primary-accessible shrink-0',
        SIZE_CLASSES[size],
        className
      )}
    >
      {name.trim().charAt(0).toLocaleUpperCase()}
    </div>
  );
}
